import Card from '../../../../../components/layouts/card/card';
import { observer } from 'mobx-react';
import { FormFieldLine } from '../../../../../components/form-field-line/form-field-line';
import shopifySettingsStore from '../../stores/shopify-settings-store';
import { Toggle } from '../../../../../components/inputs/toggle/toggle';
import { IInvalidCharactersResult, Input } from '../../../../../components/inputs/input/input';
import IconSave from '../../../../../components/icons/icon-save/icon-save';
import { Badge, BadgeType } from '../../../../../components/badge/badge';
import { ButtonIcon } from '../../../../../components/inputs/button-icon/button-icon';
import { useCallback, useEffect, useState } from 'react';
import { FONT_COLOR } from '../../../../../components/fonts/font-props';
import { IOption, SelectDropdown } from '../../../../../components/inputs/select-dropdown/select-dropdown';
import useValidationGroup from '../../../../../hooks/validation-group/validation-group-hook';
import { SingleValue } from 'react-select';
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import CurrentPortalStore from "../../../../../stores/current-portal-store";
import { Note, NOTE_TYPE } from "../../../../../components/note/note";
import Divider from '../../../../subscription-plans/components/plan-info/divider/divider';
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import { FormField } from "../../../../../components/form-field/form-field";
import PortalUrlSettings from "../../../../../images/portal-url-settings.svg";
import { ClipboardCopyInput } from '../../../../../components/inputs/clipboard-copy-input/clipboard-copy-input';
import IconPlatform from "../../../../../components/icons/icon-platform/icon-platform";
import './shopify-settings-card.scss';
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
interface IShopifySettingsProps {
    isExpandable?: boolean,
    isExpanded?: boolean,
};

const ShopifySettingsCard: React.FC<IShopifySettingsProps> = observer(({ isExpandable = false, isExpanded = true }) => {
    const [_isExpanded, _setIsExpanded] = useState<boolean>(!!isExpanded);
    const vGroup = useValidationGroup();
    const validationCallback = useCallback(() => {
        if (shopifySettingsStore.customPortalUrlErrorMessage === null) {
            return { isValid: true, messages: [] };
        }
        return { isValid: false, messages: [shopifySettingsStore.customPortalUrlErrorMessage] };
    }, [shopifySettingsStore.customPortalUrlErrorMessage]);

    
    const proxySubPathPrefixOptions = [
        { value: '/apps', label: shopifySettingsStore.storeDomain + '/apps' },
        { value: '/a', label: shopifySettingsStore.storeDomain + '/a' },
        { value: '/community', label: shopifySettingsStore.storeDomain + '/community' },
        { value: '/tools', label: shopifySettingsStore.storeDomain + '/tools' },
    ];

    const orderTrackingCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.ORDER_TRACKING);
    const isOrderTrackingEnabled = orderTrackingCapability.isSupported && orderTrackingCapability.isFeatureEnabled;

    const onSelectProxy = (singleValue: SingleValue<IOption>) => {
        if (singleValue?.value) {
            shopifySettingsStore.setProxy(singleValue.value.toString());
        }
    };

    useEffect(() => {
        shopifySettingsStore.load();
        CurrentPortalStore.load();
    }, []);

    useEffect(() => {
        _setIsExpanded(!!isExpanded);
    }, [isExpanded]);

    const onSaveClicked = () => {
        shopifySettingsStore.save();
    }

    const preventCharacters = (inputValue: string): IInvalidCharactersResult => {
        let regex = new RegExp(/[^A-Za-z0-9\-_]/);
        if (regex.test(inputValue)) {
            return { isValid: false };
        }
        else {
            return { isValid: true };
        }
    };

    const isShopify = (): boolean => {
        return CurrentPortalStore.platform?.isShopify() === true;
    }

	const shopifyIntegrationCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.SHOPIFY_INTEGRATION);

    return (
        !CurrentPortalStore.platform ? <> </> :
        <>
            <Card toolbar={{
                title: (
                    <div className='integration-title'>
                        <div className="shopify-settings-card-logo">
                            <IconPlatform exchangeTooltipText="" />
                        </div>
                        <span>{CurrentPortalStore.platform?.getPlatformDisplayName() + " Integration"}</span>
                    </div>
                ),
                content: (
                    <>
                        <ButtonIcon
                            disabled={!shopifySettingsStore.hasChanges() || shopifySettingsStore.isSaving() || !vGroup.isValid()}
                            Component={IconSave}
                            onClick={onSaveClicked}
                        />
                        {shopifySettingsStore.hasChanges() && <Badge text="unsaved" type={BadgeType.WARNING} />}
                    </>
                )
            }}
                isExpandable={isExpandable}
                isExpanded={_isExpanded || shopifySettingsStore.hasChanges()}
                isExpandDisabled={shopifySettingsStore.hasChanges()}
                expandCallback={v => _setIsExpanded(v)}
            >
                <FormFieldLine required label="Integrate into Your Store" note={
                    'Enable to activate the integration of your portals into your store.'
                }
                >
                    <Toggle disabled={!shopifySettingsStore.allowedForEnable && !shopifySettingsStore.isIntegrationEnabled}
                        checked={shopifySettingsStore.isIntegrationEnabled}
                        label=""
                        onChange={v => shopifySettingsStore.setIsIntegrationEnabled(v)}
                    />
                </FormFieldLine>

                {isShopify() &&
                     <FormFieldLine
                        label="Create RMAs as Shopify Returns"
                        note={<>
                            Enable the new way of processing Shopify returns, where returns are created natively on
                            Shopify,
                            displaying return requests on the Orders page in your Shopify admin dashboard - <a
                                href='https://support.returngo.ai/shopify-returns' target='_blank' rel='noreferrer'>learn more</a>.
                            <Note type={NOTE_TYPE.WARNING}
                                  body={"The Shopify Returns feature is limited according to Shopify's specific expectations for processing returns. Keep in mind that processing returns differently, such as refunding with store credit, may affect other Shopify features such as accounting reports and return statuses."}/>
                        </>}
                        tooltip={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                Note: This will become mandatory in the future<br />
                                and will be the only way to process returns on Shopify.
                            </FontBody2>
                        }
                    >
                        <Toggle
	                        checked={shopifySettingsStore.isUseShopifyReturnsApi}
                            label=""
	                        disabled={shopifySettingsStore.isUseShopifyReturnsApi === shopifyIntegrationCapability.enableShopifyReturnsForcedValue}
                            onChange={v => shopifySettingsStore.setUseShopifyReturnsApi(v)}
                        />
                    </FormFieldLine>
                }

                {isShopify() &&
                    <>
                        <FormFieldLine label='Portal URL Settings' note={
                            <>
                                Customize your portal URLs including the <br />
                                app proxy, app name, and portal page <br />
                                name.
                            </>
                        }>
                        <img src={PortalUrlSettings} alt=""/>
                        </FormFieldLine>
                        <FormFieldLine label='App Proxy Settings'
                                       note={
                                           <>
                                               Customize the app proxy and app name.
                                               <br/><br/>
                                               This enables Shopify to act as a proxy when your store <br/>
                                               sends a request.
                                               Learn more about <a
                                               href='https://returngo.ai/go/kb/modifying-return-portal-url'
                                               target='_blank' rel='noreferrer'>App proxy settings
                                           </a>.
                                               <br/><br/>
                                               Note: The settings here must match your <a
                                               href='https://returngo.ai/go/kb/integrating-return-portal-shopify'
                                               target='_blank' rel='noreferrer'>Shopify App Proxy settings.
                                           </a>
                                           </>
                                       }
                        >
                            <ControlGroup>
                                <FormField label={'App Proxy Settings'}>
                                    <SelectDropdown validationGroup={vGroup}
                                                    isSearchable={false}
                                                    value={proxySubPathPrefixOptions.find(o => o.value === shopifySettingsStore.proxySubPathPrefix)}
                                                    options={proxySubPathPrefixOptions}
                                                    onChange={onSelectProxy}
                                                    placeholder='Select app proxy'
                                                    required
                                    />
                                </FormField>
                                <FormField label={'App name'} required>
                                    <Input value={shopifySettingsStore.appName}
                                           onChange={(value: string) => shopifySettingsStore.setAppName(value)}
                                           required
                                           preventCharacters={preventCharacters}
                                           validationGroup={vGroup}
                                    />
                                </FormField>
                            </ControlGroup>
                        </FormFieldLine>
                        <FormFieldLine label="Return Portal Page Name" required>
                            <Input value={shopifySettingsStore.returnPortalPath}
                                   onChange={(value: string) => shopifySettingsStore.setReturnPortalPath(value)}
                                   preventCharacters={preventCharacters}
                            />
                        </FormFieldLine>
                        <FormFieldLine label="Return Portal URL" note={
                            <>
                                This is the URL to your return portal page.<br />
                                Share it with your customers via your<br />
                                store menus, policy page, or anywhere<br />
                                else on your website or emails.
                            </>
                        }>
                            <ClipboardCopyInput value={shopifySettingsStore.constructedReturnPortalUrl} placeholder={'https://{{shopify_account_name}}.myshopify.com/a/returns'} />
                        </FormFieldLine>
                    </>
                }
                <>
                    <Divider />

                    <FormFieldLine label="Custom Return Portal URL">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {isShopify() &&
                                <Toggle
                                    checked={shopifySettingsStore.isUseCustomReturnsUrl}
                                    label=""
                                    onChange={v => shopifySettingsStore.setUseCustomReturnPortalUrl(v)}
                                    disabled={!shopifyIntegrationCapability.isCustomReturnPortalUrlEditable}
                                />
                            }
                            {shopifySettingsStore.isUseCustomReturnsUrl &&
                                <Input value={shopifySettingsStore.customReturnPortalUrl}
                                    required={shopifyIntegrationCapability.isCustomReturnPortalUrlEditable}
                                    validationCallback={validationCallback}
                                    validationGroup={shopifyIntegrationCapability.isCustomReturnPortalUrlEditable ? vGroup : undefined}
                                    showValidationMessage={true}
                                    onChange={(value: string) => shopifySettingsStore.setCustomReturnPortalUrl(value)}
                                    placeholder={"{Customized URL}"}
                                    disabled={!shopifyIntegrationCapability.isCustomReturnPortalUrlEditable}
                                />                            
                            }
                        </div>
                    </FormFieldLine>
                </>
                {orderTrackingCapability.isSupported && (
                <>
                <Divider />

                {!isOrderTrackingEnabled && <Note
                    type={NOTE_TYPE.INFORMATION}
                    body={"The tracking feature is not included in your subscription plan. To activate this feature,  Contact customer support"}
                />}
                {isShopify() &&
                    <FormFieldLine label="Tracking Portal Page Name" required>
                    <Input
                        value={shopifySettingsStore.trackingPortalPath}
                        onChange={(value: string) => shopifySettingsStore.setTrackingPortalPath(value)}
                        disabled={!isOrderTrackingEnabled}
                        placeholder={!isOrderTrackingEnabled ? '--' : ''}
                    />
                    </FormFieldLine>
                }
                <FormFieldLine label="Tracking Portal URL" note={
                    <>
                        This is the URL to your tracking portal page. <br />
                        Share it with your customers via <br />
                        your store menus, policy page, or <br />
                        anywhere else on your website or <br />
                        emails.
                    </>
                }
                >
                    <ClipboardCopyInput value={isOrderTrackingEnabled ? shopifySettingsStore.constructedTrackingPortalUrl : ''}
                        placeholder={
                            isOrderTrackingEnabled ?
                                'https://{{shopify_account_name}}.myshopify.com/a/tracking' : '--'
                        }
                    />
                </FormFieldLine>
                <FormFieldLine label="Automatically track all store’s orders" note={
                    <>
                        Enable to track all store orders <br />
                        automatically, instead of only upon <br />
                        request.
                    </>
                }
                    tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        When this setting is turned on, <br />
                        all new fulfilled shipments will be <br />
                        tracked and deducted from <br />
                        your quota
                    </FontBody2>}
                >
                    <Toggle disabled={!isOrderTrackingEnabled}
                        checked={shopifySettingsStore.isAutomaticallyTrackAllOrders}
                        label=""
                        onChange={v => shopifySettingsStore.setIsAutomaticallyOrderTracking(v)}
                    />
                </FormFieldLine>

                <FormFieldLine label="Custom Order Tracking Portal URL">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }
                    }>
                        {isShopify() &&
                            <Toggle disabled={!isOrderTrackingEnabled}
                            checked={shopifySettingsStore.isUseCustomTrackingPortalUrl}
                            label=""
                            onChange={v => shopifySettingsStore.setUseCustomTrackingPortalUrl(v)}
                            />
                        }
                        {shopifySettingsStore.isUseCustomTrackingPortalUrl &&
                            <Input value={shopifySettingsStore.customTrackingPortalUrl}
                                required={shopifySettingsStore.isUseCustomTrackingPortalUrl}
                                validationGroup={shopifySettingsStore.isUseCustomTrackingPortalUrl ? vGroup : undefined}
                                disabled={!isOrderTrackingEnabled}
                                onChange={(v: string) => shopifySettingsStore.setCustomTrackingPortalUrl(v)}
                                placeholder={"{Customized URL}"}
                            />
                        }
                    </div>
                </FormFieldLine>
                </>
                )}

            </Card>
        </>
    );
});

export default ShopifySettingsCard;
